import { createComponentStore } from '../../utils/createComponentStore';
import {
  ensureCreationOutputPlaceholder,
  newCreationOutputPlaceholder,
} from '../../utils/creationOutputPlaceholder';
import { makeDefaultField } from '../pages/forms/fieldPreviews/fieldTypes';
import { normalizeFormCore, stripField } from '../pages/forms/fieldPreviews/normalizeFieldDefinitions';

const emptyData = () => ({
  // FormCore keys
  fields: [],
  title: '',
  description: '',
  // New Create Tasks default to public access + anyone-eligible assignment
  // with anonymous public-link submissions allowed, so a freshly-dragged
  // node is reachable without per-user configuration. Builders tighten
  // access from there.
  accessPolicy: { mode: 'PUBLIC', selectedUserIds: [] },
  responseMode: 'FILL_SUBMIT',
  formSettings: {},
  // ReviewTaskOverlay keys
  objective: '',
  taskConfig: {
    responsibility: {
      activeMode: 'ANYONE_ELIGIBLE',
      anyoneEligible: { allowAnonymousWhenPublicLink: true },
    },
    notifications: {
      // Email on by default; recipients/subject/body stay empty so the user
      // fills them in. The template's enable-toggle handler backfills the
      // subject/body from title/objective the first time they're observed
      // blank with email enabled, so a freshly-dragged node has sensible
      // defaults without us hardcoding template strings here.
      email: { enabled: true },
    },
  },
  // Flow-membership keys (shared across all v2 form-likes)
  stepperTitle: '',
  flowEntryId: null,
  flowConfig: null,
  // Creation-edge output placeholder. Author-renameable from the drawer;
  // auto-uniquified on first creation so multiple review_task siblings
  // never collide on the same `${taskUrl}` key.
  creationOutputPlaceholder: newCreationOutputPlaceholder('dynamic_review_task'),
});

const { getStore: getDynamicReviewTaskStore, defaultStore } = createComponentStore({
  type: 'dynamic_review_task',
  endpoint: 'dynamic-review-task',
  registryKey: 'dynamic_review_task',
  initData: emptyData,
  onLoadData: (rawData) => ({
    ...emptyData(),
    ...normalizeFormCore(rawData),
    // Overlay keys
    objective: rawData.objective || '',
    taskConfig: rawData.taskConfig || {},
    // Flow-membership keys
    stepperTitle: rawData?.stepperTitle || '',
    flowEntryId: rawData?.flowEntryId ?? null,
    flowConfig: rawData?.flowConfig ?? null,
    // Back-compat: legacy components saved before the rename UI never had
    // this field. ensureCreationOutputPlaceholder mints a fresh unique
    // key so they get a stable, unique placeholder on first edit.
    creationOutputPlaceholder: ensureCreationOutputPlaceholder(
      'dynamic_review_task',
      rawData?.creationOutputPlaceholder,
    ),
  }),
  extraActions: (set, get) => ({
    // FormCore actions
    setFields: (fields) => set({
      data: {
        ...get().data,
        fields: Array.isArray(fields) ? fields.map(stripField).filter(Boolean) : [],
      },
    }),
    setTitle: (title) => set({ data: { ...get().data, title } }),
    setDescription: (description) => set({ data: { ...get().data, description } }),
    setAccessPolicy: (accessPolicy) => set({ data: { ...get().data, accessPolicy } }),
    setResponseMode: (responseMode) => set({ data: { ...get().data, responseMode } }),
    setFormSettings: (formSettings) => set({ data: { ...get().data, formSettings: { ...get().data.formSettings, ...formSettings } } }),
    addField: () => {
      const fields = [...get().data.fields];
      const order = fields.length;
      fields.push(makeDefaultField('text', order));
      set({ data: { ...get().data, fields } });
    },
    updateField: (index, updates) => {
      const fields = [...get().data.fields];
      fields[index] = stripField({ ...fields[index], ...updates }) || fields[index];
      set({ data: { ...get().data, fields } });
    },
    removeField: (index) => {
      const fields = get().data.fields.filter((_, i) => i !== index);
      set({ data: { ...get().data, fields } });
    },
    reorderFields: (fromIndex, toIndex) => {
      const fields = [...get().data.fields];
      const [moved] = fields.splice(fromIndex, 1);
      fields.splice(toIndex, 0, moved);
      fields.forEach((f, i) => { f.order = i; });
      set({ data: { ...get().data, fields } });
    },
    // ReviewTaskOverlay actions
    setObjective: (objective) => set({ data: { ...get().data, objective } }),
    setTaskConfig: (taskConfig) => set({ data: { ...get().data, taskConfig: { ...get().data.taskConfig, ...taskConfig } } }),
    // Flow-membership actions
    setStepperTitle: (stepperTitle) => set({ data: { ...get().data, stepperTitle } }),
    setFlowSettings: ({ flowEntryId, flowConfig }) => set({
      data: { ...get().data, flowEntryId: flowEntryId ?? null, flowConfig: flowConfig ?? null },
    }),
    setCreationOutputPlaceholderKey: (key) => set({
      data: {
        ...get().data,
        creationOutputPlaceholder: {
          ...(get().data.creationOutputPlaceholder || {}),
          key,
        },
      },
    }),
  }),
});

export { getDynamicReviewTaskStore };
export default defaultStore;
